import React, { useLayoutEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import {
  clearModelPickerOverride,
  getModelPickerConfig,
  MODEL_PICKER_STORAGE_KEY,
  setModelPickerEntitlement,
} from '../../service';
import { checkIsActive } from '../../components/MainSidebar/rules';

// The "My Drafts" section, mirrored from the sidebar's My Drafts nav item
// (getFeedbackItems in components/MainSidebar/index.jsx). The picked model only
// affects feedback requested from a draft, so the picker is scoped to these pages.
const MY_DRAFTS_PATHS = [
  '/getFeedback',
  '/documents',
  '/drafts',
  '/view/submissions',
];

const readStoredProvider = () => {
  try {
    return window.localStorage.getItem(MODEL_PICKER_STORAGE_KEY) || '';
  } catch (e) {
    return '';
  }
};

// Staging-only QA surface: lets an entitled tester choose which AI provider
// serves the next feedback request. Three independent gates keep it contained:
//   1. Environment — the backend is the single source of truth. GET /jeddai/models
//      returns enabled=false in production (the server flag is never set there), so
//      this renders nothing on prod no matter what code is deployed.
//   2. Account — that authenticated endpoint enables the picker only for a
//      persisted internal account. Its result gates request plumbing in memory.
//   3. Location — even on staging it only shows on the My Drafts pages, where the
//      picked model is actually consumed.
const StagingModelPicker = ({ profile }) => {
  const location = useLocation();
  const profileId = profile?.userId || '';
  const { data } = useQuery({
    queryKey: ['modelPickerConfig', profileId],
    queryFn: getModelPickerConfig,
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: Boolean(profileId),
  });
  const accessResolved = data !== undefined;
  const entitled = data?.enabled === true;
  const [selected, setSelected] = useState(readStoredProvider);

  // The authenticated backend response is the entitlement authority. While the
  // profile/config is unresolved, close the in-memory request gate immediately
  // but preserve storage: a public deep link renders before profile hydration,
  // and must not erase an internal tester's valid saved choice. A resolved
  // disabled response proves the account is unentitled and clears it.
  useLayoutEffect(() => {
    if (!profileId || !accessResolved) {
      setModelPickerEntitlement(false, { clearOverride: false });
      return () => setModelPickerEntitlement(false, { clearOverride: false });
    }
    setModelPickerEntitlement(entitled, { clearOverride: entitled === false });
    if (!entitled) {
      clearModelPickerOverride();
      setSelected('');
    } else {
      setSelected(readStoredProvider());
    }
    return () => setModelPickerEntitlement(false, { clearOverride: false });
  }, [accessResolved, entitled, profileId]);

  if (!entitled || !data?.enabled || !data.options?.length) {
    return null;
  }

  if (!checkIsActive(location, MY_DRAFTS_PATHS)) {
    return null;
  }

  const onChange = (e) => {
    const value = e.target.value;
    setSelected(value);
    try {
      if (value) {
        window.localStorage.setItem(MODEL_PICKER_STORAGE_KEY, value);
      } else {
        clearModelPickerOverride();
      }
    } catch (error) {
      // The selection remains usable for this render; a locked-down browser
      // simply cannot persist it across reloads.
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 12,
        right: 12,
        zIndex: 2000,
        background: '#1c1c28',
        color: '#fff',
        borderRadius: 20,
        padding: '6px 12px',
        fontSize: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
        opacity: 0.92,
      }}
    >
      <span role="img" aria-label="staging model picker">
        🧪
      </span>
      <span>Model:</span>
      <select
        value={selected}
        onChange={onChange}
        style={{
          background: 'transparent',
          color: '#7fd4ff',
          border: 'none',
          fontSize: 12,
          outline: 'none',
          cursor: 'pointer',
        }}
      >
        <option value="" style={{ color: '#000' }}>
          default ({data.defaultProvider})
        </option>
        {['google', 'openai', 'anthropic', 'typesafe'].map((provider) => {
          const group = data.options.filter((o) => o.provider === provider);
          if (!group.length) return null;
          return (
            <optgroup key={provider} label={provider} style={{ color: '#000' }}>
              {group.map((o) => (
                <option key={o.key} value={o.key} style={{ color: '#000' }}>
                  {o.key === 'jev' ? 'Jev' : o.key}
                  {o.isDefault ? ' (default)' : ''}
                </option>
              ))}
            </optgroup>
          );
        })}
      </select>
    </div>
  );
};

export default StagingModelPicker;
